import { connect } from "react-redux";
import { mapStateToProps } from "../../store/helpers/mapState";
import { mapDispatchToProps } from "../../store/helpers/mapDispatch";
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Add, Circle, Delete, Edit, InfoOutlined } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { errorToast, successToast } from "../../helpers/Notification";
import { getError, getSuccessMessage } from "../../store/helpers/common_functions";
import moment from "moment";
import WorklogChat from "../../components/WorklogChat";
import DeleteDialog from "helpers/DeleteDialog";
import AttachmentManager from "../../views/admin/AttachmentManager";
import DatePicker from "react-multi-date-picker";


const WorklogManager = ({ systemSettings, worklogId, program, open, setOpen, deliverableId, subTaskId, taskId, activityId, projectId, actionTrackId, SubmitGeneralForm, FetchDetails, inline, loggedUser, canViewAll, onSuccess, canEdit = false }) => {
    const [worklogsList, setWorklogs] = useState([]);
    const [worklog, setWorklog] = useState(null);
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [edit, setEdit] = useState(false);
    const [dataToEdit, setDataToEdit] = useState({});
    const [openDelete, setOpenDelete] = useState(false);
    const [deleteURL, setDeleteURL] = useState();
    const [workLogList, setWorkLogList] = useState([]);
    const [editable, setEditable] = useState({});
    const [addNew, setAddNew] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [attachmentOpen, setAttachmentOpen] = useState(false);
    const [task, setTask] = useState({});

    const programId = program?.id;

    const mainBackground = systemSettings?.mainBackground;


    const formRef = useRef(null);

    const type = activityId ? 'activity' : program ? 'program' : actionTrackId ? 'action' : deliverableId ? 'deliverable' : worklogId ? 'worklog' : subTaskId ? 'subTask' : (taskId ? 'task' : (projectId ? 'project' : 'none'));
    const id = subTaskId ?? taskId ?? projectId ?? deliverableId ?? actionTrackId ?? worklogId ?? programId ?? activityId;
    const STEP = 4;

    useEffect(() => {
        setWorkLogList(worklogsList?.slice(0, STEP));
    }, [worklogsList]);

    const loadMoreLog = () => {
        setWorkLogList([
            ...workLogList,
            ...worklogsList.slice(
                workLogList?.length,
                workLogList?.length + STEP
            ),
        ]);
        setLoading(false);
    };

    const loadWorklogs = async () => {
        setLoadingLogs(true);
        const resp = await FetchDetails(loggedUser?.type === 'PARTNER' ? `/api/worklogs/list/${id}/${type}?userId=${loggedUser?.id}` : `/api/worklogs/list/${id}/${type}`);
        setLoadingLogs(false);
        if (resp.status) {
            const allList = canViewAll ? resp.data?.data : resp.data?.data?.filter((myLog) => myLog?.resourceId === loggedUser?.resourceId);
            setWorklogs(allList);
            setInfo(resp.data?.info);
        }
    };

    useEffect(() => {
        if (id) {
            loadWorklogs();
        }
    }, [id]);

    const submitForm = async (data) => {
        const getTextFromHTML = (html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return doc.body.textContent?.replace(/\s+/g, '').trim() || "";
        };

        const trimmedDescription = data.description ? getTextFromHTML(data.description) : '';

        if (!trimmedDescription) {
            setValidationErrors({ description: "Description is required" });
            return;
        }
        if (trimmedDescription.length < 2) {
            setValidationErrors({ description: "Description must be at least 2 characters long" });
            return;
        }
        const worklogData = {
            ...data,
            ...(subTaskId ? { subTaskId } : (taskId ? { taskId } : {})),
            projectId,
            deliverableId,
            actionTrackId,
            worklogId,
            programId: program?.id,
            activityId,
            // startDate: (data.startDate).format('YYYY-MM-DD'), // Save date in 'YYYY-MM-DD' format
            from: data.from, // Save 'from' time
            to: data.to, // Save 'to' time
            duration: calculateDuration(data.from, data.to), // Calculate duration
            userId: loggedUser?.id,
        };

        try {
            const resp = await SubmitGeneralForm(`/api/worklogs/create`, worklogData);
            if (resp.status) {
                formRef.current?.reset();
                loadWorklogs();
                successToast(getSuccessMessage(resp));
                setEdit(false);
                setDataToEdit({});
                setValidationErrors({});
            } else {
                errorToast(getError(resp));
            }
        } catch (err) {
            errorToast("An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };

    const calculateDuration = (from, to) => {
        if (!from || !to) {
            return "0h:0m";
        }

        const [fromHours, fromMinutes] = from.split(':').map(Number);
        const [toHours, toMinutes] = to.split(':').map(Number);

        const startTime = moment().hours(fromHours).minutes(fromMinutes);
        const endTime = moment().hours(toHours).minutes(toMinutes);

        const totalMinutes = endTime.diff(startTime, 'minutes');
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        return `${hours}h:${minutes}m`;
    };


    const formConfig = {
        title: edit ? 'Edit Worklog' : 'Create Worklog',
        mainFields: [
            {
                title: '',
                fields: [
                    {
                        name: 'startDate',
                        label: 'Date',
                        required: true,
                        type: 'date',
                        value: dataToEdit?.startDate
                            ? dataToEdit.startDate
                            // moment().format('YYYY-MM-DD')
                            : '',
                        setValue: (val) => {
                            setDataToEdit((prev) => ({
                                ...prev,
                                startDate: val,
                            }));
                        }
                    },
                    {
                        name: 'from',
                        label: 'From',
                        type: 'time',
                        value: dataToEdit?.from || '',
                        required: true,
                        setValue: (val) => {
                            const to = dataToEdit?.to || '';
                            setDataToEdit((prev) => ({
                                ...prev,
                                from: val,
                                duration: to ? calculateDuration(val, to) : '',
                            }));
                        },
                    },
                    {
                        name: 'to',
                        label: 'To',
                        type: 'time',
                        value: dataToEdit?.to || '',
                        required: true,
                        setValue: (val) => {
                            const from = dataToEdit?.from || '';
                            if (from && moment(val, 'HH:mm').isBefore(moment(from, 'HH:mm'))) {
                                errorToast('The "To" time must be later than the "From" time.');
                                return;
                            }
                            setDataToEdit((prev) => ({
                                ...prev,
                                to: val,
                                duration: from ? calculateDuration(from, val) : '',
                            }));
                        },
                    },
                    {
                        name: 'duration',
                        label: 'Duration',
                        type: 'text',
                        value: dataToEdit?.duration || '',
                        disabled: true,
                        required: true,
                    },
                    {
                        name: 'description',
                        label: 'Description',
                        value: dataToEdit?.description || '',
                        required: false,
                        type: 'textarea',
                        col: 12,
                        setValue: (val) => setDataToEdit((prev) => ({
                            ...prev,
                            description: val,
                        })),
                    },
                ],
            },
        ],
    };

    const renderFormFields = () => (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                {formConfig.mainFields[0].fields.filter(field => field.name !== 'description').map((field) => (
                    <div className={'mb-3 px-2 flex-grow'} key={field.name} style={{ flex: '1 1 25%', marginBottom: '1rem' }}>
                        <label className="col-form-label fw-bold text-capitalize" style={{ marginBottom: '0.5rem' }}>
                            {field.label}
                            {field.required && <span className="text-red-500 fs-6">*</span>}
                        </label>
                        {field.name === 'startDate' ? (
                            <DatePicker
                                value={field.value}
                                onChange={(value) => field.setValue(value)}
                                format="YYYY-MM-DD"
                                minDate={moment().subtract(0, 'days').toDate()}
                                maxDate={moment().toDate()}
                                inputFormat="YYYY-MM-DD"
                                placeholder={field.label}
                                required={field.required}
                                style={{ padding: '1.3rem', borderRadius: '4px', border: '1px solid #ccc' }}
                            />
                        ) :
                            <input
                                type={field.type || 'text'}
                                value={field.value}
                                onChange={(e) => field.setValue(e.target.value)}
                                className="form-control"
                                placeholder={field.label}
                                required={field.required}
                                disabled={field.disabled}
                                style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                            />}
                    </div>
                ))}
            </div>
            <div className={'mb-3 px-2 flex-grow'} style={{ flex: '1 1 100%', marginBottom: '1rem' }}>
                {formConfig.mainFields[0].fields.filter(field => field.name === 'description').map((field) => (
                    <div key={field.name}>
                        <label className="col-form-label fw-bold text-capitalize" style={{ marginBottom: '0.5rem' }}>
                            {field.label}
                            {field.required && <span className="text-red-500 fs-6">*</span>}
                        </label>
                        <br />

                        {/* <JoditEditorExample
                            htmlContent={dataToEdit?.description ?? ''}
                            onBlur={(str) => {
                                const newValue = str.target.value;
                                if (dataToEdit?.description !== newValue) {
                                    setDataToEdit({ ...dataToEdit, description: newValue });
                                }
                            }}
                        /> */}
                        <textarea
                            value={dataToEdit?.description ?? ''}
                            onChange={(e) => setDataToEdit({ ...dataToEdit, description: e.target.value })}
                            className="form-control"
                            // placeholder={field.label}
                            required={field.required}
                            rows={5}
                            style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc', width: '100%', boxSizing: 'border-box', minHeight: '120px' }}
                        />
                        {validationErrors.description && (
                            <div style={{ color: 'red', marginTop: '0.5rem' }}>
                                {validationErrors.description}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    const form = (
        <div>
            {addNew ? null : <Button variant="outlined"
                className="float-end"
                sx={{ color: "white", backgroundColor: mainBackground ? mainBackground : "#336767",
                    "&:hover": {
                        backgroundColor: mainBackground ? mainBackground : "#336767", color: "white",
                    },
                }}
                onClick={() => setAddNew(true)} startIcon={<Add />}>Record New</Button>} <br /><br />
            {addNew ? <form ref={formRef} onSubmit={e => {
                e.preventDefault();
                submitForm({
                    ...dataToEdit,
                    taskId,
                    projectId,
                    // deliverableId,
                    actionTrackId,
                    subTaskId,
                    programId: program?.id,
                    activityId
                });
            }}>
                {renderFormFields()}
                <div className={'px-2'} style={{ display: 'flex', marginTop: '1rem' }}>
                    {loading ? <CircularProgress /> :
                        <>
                            <Button
                                variant={'contained'}
                                color="error"
                                onClick={() => {
                                    setTimeout(() => {
                                        setEdit(false);
                                        setDataToEdit({});
                                        setAddNew(false);
                                        setValidationErrors({});
                                    }, 100);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button style={{ marginLeft: '5px', backgroundColor: mainBackground }} variant={'contained'} type={'submit'}>
                                {edit ? 'Apply Change' : 'Create'}
                            </Button>


                        </>
                    }
                </div>
            </form> : null}
        </div>
    );

    const content = loadingLogs ? (
        <div style={{ height: "400px" }} className={'flex items-center justify-center'}>
            <CircularProgress />
        </div>
    ) : (
        <>
            {edit ? null : form}
            <hr className={'mt-3 mb-3'} />
            {!worklogsList.length ? (
                <div style={{ height: "400px" }} className={'flex items-center justify-center'}>
                    <div className={'text-center'}>
                        <InfoOutlined fontSize={'large'} />
                        <div>No worklogs recorded so far!</div>
                    </div>
                </div>
            ) : (
                <div>
                    {workLogList
                        .map(v =>
                            <Card className={'worklogs-list mb-2'} onMouseEnter={() => setEditable({ edit: true, index: v?.id })} onMouseLeave={() => setEditable({})}>
                                <CardHeader
                                    className="worklogs-header"
                                    subheader={<span style={{ color: '#0f77b4' }}>{v?.resourceId === loggedUser.resourceId ? null : v?.resource?.names}</span>}
                                    action={edit ? null : v?.resourceId === loggedUser.resourceId && editable?.edit && editable?.index === v?.id && <>
                                        {
                                            !(v.startDate && v.endDate && v.from == null) ?
                                                <Edit fontSize="small" color="info" role="button" onClick={() => {
                                                    setTimeout(() => { // Add a timeout to handle the state updates
                                                        setEdit(true);
                                                        setAddNew(true);
                                                        setDataToEdit({
                                                            ...v,

                                                            fromTime: v.startDate ? moment(v.startDate).format("HH:mm") : '',
                                                            endTime: v.endDate ? moment(v.endDate).format("HH:mm") : '',
                                                        });
                                                    }, 100);
                                                }} /> : null
                                        }

                                        <Delete fontSize="small" color="error" role="button" onClick={() => {
                                            setOpenDelete(true);
                                            setDeleteURL(`/api/worklogs/${v?.id}`)
                                        }} />
                                    </>}
                                />
                                <CardContent>
                                    {edit && dataToEdit?.id === v?.id ? form :
                                        <div className={`p-2`}>
                                            <p className={'mb-2'}>
                                                <div dangerouslySetInnerHTML={{ __html: v.description }} />
                                            </p>
                                            <div className={'flex items-center'}>
                                                <span style={{
                                                    fontSize: "12px",
                                                    color: "#5e5e5e"
                                                }}>
                                                    {v.startDate && v.endDate ? (
                                                        (moment(v.startDate).format("YYYY-MM-DD") !== moment(v.endDate).format("YYYY-MM-DD")) && v.from == null ? (
                                                            <>
                                                                Date Range: {moment(v.startDate).format("YYYY-MM-DD")} to {moment(v.endDate).format("YYYY-MM-DD")}
                                                                <span style={{ marginLeft: '30px' }}>
                                                                    <span className={'px-3'} style={{ fontSize: '8px', color: "#5e5e5e" }}>
                                                                        <Circle fontSize={'inherit'} color={'inherit'} />
                                                                    </span>
                                                                    Duration: {moment(v.endDate).diff(moment(v.startDate), 'days')} days
                                                                </span>
                                                            </>
                                                        ) : (
                                                            (moment(v.startDate).format("YYYY-MM-DD") === moment(v.endDate).format("YYYY-MM-DD")) && v.from == null ? (
                                                                <>
                                                                    Date: {moment(v.startDate).format("YYYY-MM-DD")}
                                                                    <span style={{ marginLeft: '30px' }}>
                                                                        <span className={'px-3'} style={{ fontSize: '8px', color: "#5e5e5e" }}>
                                                                            <Circle fontSize={'inherit'} color={'inherit'} />
                                                                        </span>
                                                                        {moment(v.startDate).format("HH:mm")} - {moment(v.endDate).format("HH:mm")}
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    Date: {moment(v.startDate).format("YYYY-MM-DD")}
                                                                    <span style={{ marginLeft: '30px' }}>
                                                                        <span className={'px-3'} style={{ fontSize: '8px', color: "#5e5e5e" }}>
                                                                            <Circle fontSize={'inherit'} color={'inherit'} />
                                                                        </span>
                                                                        {v.from && v.to ? (
                                                                            `${v.from} - ${v.to}`
                                                                        ) : (
                                                                            v.from
                                                                        )}
                                                                    </span>
                                                                </>
                                                            )
                                                        )) : (
                                                        <>
                                                            Date: {moment(v.startDate).format("YYYY-MM-DD")}
                                                            <span style={{ marginLeft: '30px' }}>
                                                                <span className={'px-3'} style={{ fontSize: '8px', color: "#5e5e5e" }}>
                                                                    <Circle fontSize={'inherit'} color={'inherit'} />
                                                                </span>
                                                                {v.from && v.to ? (
                                                                    `${moment(v.from, "HH:mm").format("h:mm A")} - ${moment(v.to, "HH:mm").format("h:mm A")}`
                                                                ) : (
                                                                    v.from
                                                                )}
                                                            </span>
                                                        </>
                                                    )}

                                                </span>

                                                <span className={'px-3'} style={{ fontSize: '8px', color: "#5e5e5e" }}>
                                                    {/* <Circle fontSize={'inherit'} color={'inherit'} /> */}
                                                </span>
                                                {!(v.startDate && v.endDate && v.from == null) ? (<span className={'px-3'} style={{ fontSize: '8px', color: "#5e5e5e" }}>
                                                    <Circle fontSize={'inherit'} color={'inherit'} />
                                                </span>) : null}


                                                <span style={{ fontSize: "12px", color: "#5e5e5e" }}>
                                                    {(() => {
                                                        // if ((v.startDate && v.endDate)) {
                                                        //     // Calculate duration if both startDate and endDate exist
                                                        if (((moment(v.startDate).format("YYYY-MM-DD") !== moment(v.endDate).format("YYYY-MM-DD")) && v.from == null && v.to == null)) {
                                                            // Calculate duration if both startDate and endDate exist days
                                                            const start = moment(v.startDate);
                                                            const end = moment(v.endDate);
                                                            const duration = moment.duration(end.diff(start));

                                                            const days = duration.days(); // Total days

                                                            // return `${days}`;

                                                        }
                                                        else if (v.from && v.to) {
                                                            return v.duration;
                                                        }
                                                        else if (moment(v.startDate).format("YYYY-MM-DD") === moment(v.endDate).format("YYYY-MM-DD") && v.from == null) {

                                                            // calculate duration if both startDate and endDate exist
                                                            const start = moment(v.startDate);
                                                            const end = moment(v.endDate);
                                                            const duration = moment.duration(end.diff(start));

                                                            const hours = Math.floor(duration.asHours()); // Total hours
                                                            const minutes = duration.minutes(); // Remaining minutes

                                                            return `${hours} hours ${minutes} minutes`;
                                                        } else if (v.duration) {
                                                            return v.duration;
                                                        } else {
                                                            return "Duration not available";
                                                        }
                                                    })()}
                                                </span>

                                                <div className="flex items-center" style={{ marginLeft: '50px' }}>
                                                    <span className={'px-3'} style={{ fontSize: '8px', color: "#5e5e5e" }}>
                                                        <Circle fontSize={'inherit'} color={'inherit'} />
                                                    </span>
                                                    <Link onClick={e => {
                                                        e.preventDefault();
                                                        setWorklog(v);
                                                    }} to={'chat'} style={{ fontSize: "12px", color: "#5e5e5e" }}>({v.count}) chats</Link>
                                                </div>
                                                <div className="flex items-center" style={{ marginLeft: '50px' }}>
                                                    <span className={'px-3'} style={{ fontSize: '8px', color: "#5e5e5e" }}>
                                                        <Circle fontSize={'inherit'} color={'inherit'} />
                                                    </span>
                                                    <Link onClick={e => {
                                                        e.preventDefault();
                                                        setAttachmentOpen(v);
                                                        setWorklog(v)
                                                    }} to={'attachment'} style={{ fontSize: "12px", color: "#5e5e5e" }}>({v.attachmentsCount}) Attachments</Link>
                                                </div>
                                                <div className="flex items-center gap-4 px-2">
                                                    {task?.task_attachments?.length > 0 && (
                                                        <Tooltip >
                                                            <div className="relative flex items-center">
                                                                <span
                                                                    style={{
                                                                        top: "-6px",
                                                                        right: "-10px",
                                                                        padding: "2px",
                                                                        fontSize: "10px",
                                                                        backgroundColor: mainBackground,
                                                                    }}
                                                                    className="absolute  bg-red-600 text-white rounded-full font-bold flex items-center justify-center">
                                                                    {task.task_attachments.length}
                                                                </span>
                                                                <i className="fas fa-paperclip text-blueGray-500 text-lg"></i>
                                                            </div>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </div>
                                        </div>}
                                </CardContent>
                            </Card>
                        )}
                    {worklogsList?.length !== workLogList?.length && !loading ? (
                        <Button
                            size="small"
                            className="mt-3"
                            onClick={() => {
                                setLoading(true);
                                loadMoreLog();
                            }}
                        >
                            <u>Load more</u>
                        </Button>
                    ) : loading ? (
                        <CircularProgress />
                    ) : null}
                    {worklog != null && (
                        <div>
                            <WorklogChat
                                worklog={worklog}
                                inline={true}
                                open={worklog != null && !attachmentOpen}
                                onClose={() => setWorklog(null)}
                                onSuccess={loadWorklogs}
                            />
                        </div>
                    )}
                    {worklog != null && (
                        <div>
                            <AttachmentManager
                                worklogId={worklogId}
                                isWorklog={true}
                                worklog={worklog}
                                openAttachment={attachmentOpen}
                                onClose={() => {
                                    setAttachmentOpen(false)
                                    setWorklog(null);
                                }
                                }
                                onSuccess={loadWorklogs}
                                canViewAll={canEdit} />
                        </div>
                    )}

                </div>
            )}
        </>
    );

    return (
        <div>
            {inline ? content : (
                // <Dialog open={open} maxWidth={'lg'} fullWidth={true} onClose={() => setOpen(false)}>
                //     <DialogTitle>Worklog Information ({type} : {info?.name ?? info?.title ?? "..."})</DialogTitle>
                //     <DialogContent>{content}</DialogContent>
                // </Dialog>

                <Dialog open={open} maxWidth={'lg'} fullWidth={true} onClose={() => setOpen(false)}>
                    <div className={"flex items-center justify-between"}>
                        <DialogTitle>Worklog Information ({type} : {info?.name ?? info?.title ?? "..."})</DialogTitle>
                        <IconButton
                            className="right-2"
                            onClick={
                                () => {
                                    setOpen(false);
                                    setWorklog(null);
                                }
                            }>
                            <img
                                src={require("assets/icons/close.svg").default}
                                alt={"close"}
                            />
                        </IconButton>
                    </div>

                    <DialogContent>{content}</DialogContent>
                </Dialog>
            )}
            <DeleteDialog open={openDelete} setOpen={setOpenDelete} url={deleteURL} onSuccess={() => { loadWorklogs(); setDeleteURL(''); }} />
        </div>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(WorklogManager);
